import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { CONFERENCIA_STATUS_LABELS, formatDataLocal, type ConferenciaStatusKey } from '@agencia/shared';
import { ConferirReserva } from '@/components/ConferirReserva';
import { RoboConferencia } from '@/components/RoboConferencia';
import { CiaSelo } from '@/components/CiaSelo';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useApi } from '@/lib/useApi';

interface TrechoConf {
  id: string;
  companhia: string;
  numeroVoo: string;
  origem: string;
  destino: string;
  partida: string;
  chegada: string | null;
  passou: boolean;
}

interface ReservaConf {
  id: string;
  localizador: string;
  companhia: string;
  companhiaNome: string;
  sobrenome: string | null;
  passageiros: string[];
  cliente: { id: string; nome: string; telefone: string | null };
  consultor: { id: string; nome: string } | null;
  proximaPartida: string | null;
  consultaUrl: string | null;
  abreDireto: boolean;
  faltaSobrenome: boolean;
  conferidaHoje: boolean;
  ultimaConferenciaEm: string | null;
  ultimaConferenciaStatus: ConferenciaStatusKey | null;
  trechos: TrechoConf[];
  historico: { id: string; status: ConferenciaStatusKey; observacao: string | null; mudancas: { voo: string; antes: string; depois: string }[] | null; conferidoPor: string; createdAt: string }[];
}

interface ListaConf {
  total: number;
  conferidasHoje: number;
  reservas: ReservaConf[];
}

const fmtHora = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Bahia', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const CLASSE: Record<ConferenciaStatusKey, string> = { OK: 'pill--ok', CANCELADA: 'pill--bad', ALTERADA: 'pill--warn', NAO_ENCONTRADA: 'pill--bad' };

/** Conferência diária das reservas no site da companhia, direto da base do sistema. */
export function ConferenciaReservas({ dias, consultor, busca, mostrarConsultor, gestao = false, onConferiu }: { dias: number; consultor: string; busca: string; mostrarConsultor: boolean; gestao?: boolean; onConferiu: () => void }) {
  const qs = new URLSearchParams({ dias: String(dias), ...(consultor ? { consultor } : {}) });
  const { data, error, reload } = useApi<ListaConf>(`/crm/conferencia?${qs.toString()}`);
  const [soFaltam, setSoFaltam] = useState(true);
  // Robô de conferência: botão e painel só aparecem com o robô ligado em alguma companhia.
  const { data: robo } = useApi<{ cias: string[] }>('/crm/conferencia/avulsa/cias');
  const roboLigado = Boolean(robo?.cias.length);

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (data?.reservas ?? []).filter(
      (r) => (!soFaltam || !r.conferidaHoje) && (!q || [r.localizador, r.cliente.nome, r.sobrenome ?? '', ...r.passageiros].some((s) => s.toLowerCase().includes(q))),
    );
  }, [data, busca, soFaltam]);

  const pct = data && data.total ? Math.round((data.conferidasHoje / data.total) * 100) : 0;

  return (
    <div className="conf">
      {error && <Alert>{error}</Alert>}
      {roboLigado && (
        <div className="conf__topo">
          <ConferirReserva
            onGravou={() => {
              void reload();
              onConferiu();
            }}
          />
        </div>
      )}
      {gestao && roboLigado && (
        <RoboConferencia
          dias={dias}
          onAtualizou={() => {
            void reload();
            onConferiu();
          }}
        />
      )}
      {data && (
        <section className="card conf__progresso">
          <div className="row between">
            <div>
              <strong>
                Conferidas hoje: {data.conferidasHoje} de {data.total}
              </strong>
              <p className="muted small">Abra a reserva no site da companhia, confira e marque o resultado. Cancelada, alterada ou não encontrada vira alerta para avisar o cliente.</p>
            </div>
            <label className="switch small">
              <input type="checkbox" checked={soFaltam} onChange={(e) => setSoFaltam(e.target.checked)} />
              <span>Só as que faltam hoje</span>
            </label>
          </div>
          <div className="conf__barra" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Reservas conferidas hoje">
            <div style={{ width: `${pct}%` }} />
          </div>
        </section>
      )}
      {!data ? (
        <p className="muted">Carregando…</p>
      ) : lista.length === 0 ? (
        <section className="card">
          <p className="muted">{data.total === 0 ? `Nenhuma reserva com voo nos próximos ${dias} dias.` : soFaltam ? 'Todas as reservas do período já foram conferidas hoje. 👍' : 'Nenhuma reserva encontrada.'}</p>
        </section>
      ) : (
        <div className="conf__lista">
          {lista.map((r) => (
            <LinhaConferencia
              key={r.id}
              r={r}
              mostrarConsultor={mostrarConsultor}
              onSaved={() => {
                void reload();
                onConferiu();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LinhaConferencia({ r, mostrarConsultor, onSaved }: { r: ReservaConf; mostrarConsultor: boolean; onSaved: () => void }) {
  const [modo, setModo] = useState<null | 'CANCELADA' | 'NAO_ENCONTRADA' | 'ALTERADA'>(null);
  const [obs, setObs] = useState('');
  const futuros = r.trechos.filter((t) => !t.passou);
  const [horarios, setHorarios] = useState(() => Object.fromEntries(futuros.map((t) => [t.id, { partida: t.partida, chegada: t.chegada ?? '' }])));
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  async function salvar(status: ConferenciaStatusKey) {
    setBusy(true);
    setErro(null);
    try {
      await api.post(`/crm/reservas/${r.id}/conferencia`, {
        status,
        observacao: obs.trim() || null,
        ...(status === 'ALTERADA' ? { horarios: Object.entries(horarios).map(([trechoId, h]) => ({ trechoId, partida: h.partida, chegada: h.chegada || null })) } : {}),
      });
      setModo(null);
      setObs('');
      onSaved();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function abrir() {
    try {
      await navigator.clipboard.writeText(r.sobrenome ? `${r.localizador} ${r.sobrenome}` : r.localizador);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* sem permissão para copiar: o localizador está na tela */
    }
  }

  const ultima = r.historico[0];
  return (
    <article className={`conf__item ${r.conferidaHoje ? 'is-feita' : ''}`}>
      <header className="conf__top">
        <CiaSelo companhia={r.companhia} nome={r.companhiaNome} />
        <div className="conf__id">
          <strong className="voo__loc">{r.localizador}</strong>
          <span className="small">
            {r.sobrenome && <strong>{r.sobrenome}</strong>} · <Link to={`/clientes/${r.cliente.id}`}>{r.cliente.nome}</Link>
            {mostrarConsultor && <span className="muted"> · {r.consultor?.nome ?? 'sem consultor'}</span>}
          </span>
          <ul className="conf__trechos" aria-label="Voos da reserva">
            {r.trechos.map((t) => (
              <li key={t.id} className={t.passou ? 'is-passou' : ''}>
                <strong>
                  {t.origem} → {t.destino}
                </strong>
                <span>
                  {formatDataLocal(t.partida)} · {t.partida.slice(11, 16)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="conf__estado small">
          {r.conferidaHoje && ultima ? (
            <span className={`pill ${CLASSE[ultima.status]}`}>
              {CONFERENCIA_STATUS_LABELS[ultima.status]} · hoje {fmtHora.format(new Date(ultima.createdAt)).slice(-5)}
            </span>
          ) : (
            <span className="pill pill--gray">Não conferida hoje</span>
          )}
          {!r.conferidaHoje && ultima && (
            <span className="muted">
              última: {CONFERENCIA_STATUS_LABELS[ultima.status]} em {fmtHora.format(new Date(ultima.createdAt))}
            </span>
          )}
        </div>
      </header>

      {erro && <Alert>{erro}</Alert>}
      {r.faltaSobrenome && (
        <p className="small conf__aviso">
          O bilhete veio com o sobrenome escondido. <Link to={`/clientes/${r.cliente.id}`}>Preencha o sobrenome na reserva</Link> para a LATAM abrir direto.
        </p>
      )}

      <div className="conf__acoes">
        {r.consultaUrl ? (
          <a className="btn btn--secondary btn--sm" href={r.consultaUrl} target="_blank" rel="noreferrer" onClick={() => (r.abreDireto ? undefined : void abrir())}>
            {copiado ? 'Localizador copiado — cole no site' : r.abreDireto ? `Abrir reserva na ${r.companhiaNome}` : `Abrir na ${r.companhiaNome} (copia o localizador)`}
          </a>
        ) : (
          <span className="muted small">Abra no site da companhia</span>
        )}
        <span className="conf__sep" aria-hidden="true" />
        <button type="button" className="btn btn--sm conf__btn conf__btn--ok" disabled={busy} onClick={() => void salvar('OK')}>
          ✓ OK
        </button>
        <button type="button" className={`btn btn--sm conf__btn conf__btn--alt ${modo === 'ALTERADA' ? 'is-on' : ''}`} disabled={busy} onClick={() => setModo(modo === 'ALTERADA' ? null : 'ALTERADA')}>
          Alterada
        </button>
        <button type="button" className={`btn btn--sm conf__btn conf__btn--bad ${modo === 'CANCELADA' ? 'is-on' : ''}`} disabled={busy} onClick={() => setModo(modo === 'CANCELADA' ? null : 'CANCELADA')}>
          Cancelada
        </button>
        <button type="button" className={`btn btn--sm btn--ghost ${modo === 'NAO_ENCONTRADA' ? 'is-on' : ''}`} disabled={busy} onClick={() => setModo(modo === 'NAO_ENCONTRADA' ? null : 'NAO_ENCONTRADA')}>
          Não encontrada
        </button>
      </div>

      {modo && (
        <div className={`conf__painel ${modo === 'ALTERADA' ? '' : 'is-bad'}`}>
          {modo === 'ALTERADA' && (
            <>
              <p className="small">
                <strong>Novos horários</strong> (hora local de cada aeroporto). Mude só o que a companhia alterou:
              </p>
              {futuros.map((t) => (
                <div key={t.id} className="conf__horario">
                  <span className="small">
                    <strong>
                      {t.companhia} {t.numeroVoo}
                    </strong>{' '}
                    {t.origem}→{t.destino}
                  </span>
                  <label className="small">
                    Saída
                    <input type="datetime-local" value={horarios[t.id]?.partida ?? ''} onChange={(e) => setHorarios((h) => ({ ...h, [t.id]: { ...h[t.id]!, partida: e.target.value } }))} />
                  </label>
                  <label className="small">
                    Chegada
                    <input type="datetime-local" value={horarios[t.id]?.chegada ?? ''} onChange={(e) => setHorarios((h) => ({ ...h, [t.id]: { ...h[t.id]!, chegada: e.target.value } }))} />
                  </label>
                </div>
              ))}
            </>
          )}
          {modo !== 'ALTERADA' && (
            <p className="small">
              <strong>{modo === 'CANCELADA' ? 'Confirmar que a reserva está cancelada?' : 'Confirmar que a reserva não aparece no site?'}</strong> Os voos dela vão ficar com alerta para avisar o cliente.
            </p>
          )}
          <input className="conf__obs" maxLength={300} placeholder="Observação (opcional): o que você viu no site" value={obs} onChange={(e) => setObs(e.target.value)} />
          <div className="row tight">
            <Button variant={modo === 'ALTERADA' ? 'primary' : 'danger'} loading={busy} onClick={() => void salvar(modo)}>
              {modo === 'ALTERADA' ? 'Salvar alteração' : modo === 'CANCELADA' ? 'Confirmar cancelada' : 'Confirmar não encontrada'}
            </Button>
            <Button variant="ghost" onClick={() => setModo(null)}>
              Voltar
            </Button>
          </div>
        </div>
      )}

      {r.historico.length > 0 && (
        <details className="conf__hist small">
          <summary>Histórico de conferências ({r.historico.length})</summary>
          <ul className="plain-list">
            {r.historico.map((h) => (
              <li key={h.id}>
                <span className={`pill ${CLASSE[h.status]}`}>{CONFERENCIA_STATUS_LABELS[h.status]}</span> {fmtHora.format(new Date(h.createdAt))} · {h.conferidoPor}
                {h.mudancas?.map((m) => (
                  <span key={m.voo} className="muted">
                    {' '}
                    · {m.voo}: {m.antes} → {m.depois}
                  </span>
                ))}
                {h.observacao && <span className="muted"> · “{h.observacao}”</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </article>
  );
}
